import React from "react";
import { useTranslation } from "react-i18next";
import { WorkPriority, WorkStatus, WorkType } from "../../types/works";

/**
 * Self-contained badge styling — the Works module intentionally doesn't reuse
 * the shipment/task status colors since its status set is its own.
 */

const STATUS_STYLES: Record<WorkStatus, string> = {
  draft: "bg-[#F5F4F0] text-[#8A9590]",
  planned: "bg-[#EAF1FB] text-[#3B6FB6]",
  in_progress: "bg-[#FCEEE2] text-[#C97A3C]",
  paused: "bg-[#F5F0E2] text-[#A98A3C]",
  completed: "bg-[#E8F3EC] text-[#3F9461]",
  overdue: "bg-[#FBEAEA] text-[#E24B4A]",
  cancelled: "bg-[#F0F0F0] text-[#8A9590] line-through",
};

const PRIORITY_STYLES: Record<WorkPriority, string> = {
  low: "bg-[#F5F4F0] text-[#8A9590]",
  normal: "bg-[#EAF1FB] text-[#3B6FB6]",
  high: "bg-[#FCEEE2] text-[#C97A3C]",
  urgent: "bg-[#FBEAEA] text-[#E24B4A]",
};

const TYPE_STYLES: Record<WorkType, string> = {
  cyclic: "bg-[#EAF1FB] text-[#3B6FB6]",
  object: "bg-[#F1ECF8] text-[#7B5BB6]",
  pickup_delivery: "bg-[#FCEEE2] text-[#C97A3C]",
};

const baseBadge = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap";

export const WorkStatusBadge: React.FC<{ status: WorkStatus }> = ({ status }) => {
  const { t } = useTranslation("works");
  return (
    <span className={`${baseBadge} ${STATUS_STYLES[status]}`}>
      {t(`status.${status}`)}
    </span>
  );
};

export const WorkPriorityBadge: React.FC<{ priority: WorkPriority }> = ({ priority }) => {
  const { t } = useTranslation("works");
  if (priority === "normal") return null;
  return (
    <span className={`${baseBadge} ${PRIORITY_STYLES[priority]}`}>
      {t(`priority.${priority}`)}
    </span>
  );
};

export const WorkTypeBadge: React.FC<{ type: WorkType }> = ({ type }) => {
  const { t } = useTranslation("works");
  return (
    <span className={`${baseBadge} ${TYPE_STYLES[type]}`}>
      {t(`type.${type}`)}
    </span>
  );
};
